import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { HelmetProvider } from "react-helmet-async";
import Index from "./pages/Index";
import EasterEggs from "./components/EasterEggs";
import LoadingScreen from "./components/LoadingScreen";
import ScrollProgress from "./components/ScrollProgress";

// Route-split below-the-fold pages — only Index loads in the initial bundle
const Event = lazy(() => import("./pages/Event"));
const Venue = lazy(() => import("./pages/Venue"));
const Hotel = lazy(() => import("./pages/Hotel"));
const Travel = lazy(() => import("./pages/Travel"));
const Vendors = lazy(() => import("./pages/Vendors"));
const PreRegister = lazy(() => import("./pages/PreRegister"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const Auth = lazy(() => import("./pages/Auth"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminClubs = lazy(() => import("./pages/AdminClubs"));
const AdminClubDetail = lazy(() => import("./pages/AdminClubDetail"));
const AdminClubBranding = lazy(() => import("./pages/AdminClubBranding"));
const AdminLevels = lazy(() => import("./pages/AdminLevels"));
const AdminEmails = lazy(() => import("./pages/AdminEmails"));
const AdminRequests = lazy(() => import("./pages/AdminRequests"));
const AdminBulkImport = lazy(() => import("./pages/AdminBulkImport"));
const Portal = lazy(() => import("./pages/Portal"));
const PortalLogin = lazy(() => import("./pages/PortalLogin"));
const PortalSetPassword = lazy(() => import("./pages/PortalSetPassword"));
const NotFound = lazy(() => import("./pages/NotFound"));

const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Suspense fallback={<div className="fixed inset-0 bg-background" aria-hidden />}>
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<Index />} />
          <Route path="/event" element={<Event />} />
          <Route path="/venue" element={<Venue />} />
          <Route path="/hotel" element={<Hotel />} />
          <Route path="/travel" element={<Travel />} />
          <Route path="/vendors" element={<Vendors />} />
          <Route path="/pre-register" element={<PreRegister />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/admin/clubs" element={<AdminClubs />} />
          <Route path="/admin/clubs/:id" element={<AdminClubDetail />} />
          <Route path="/admin/clubs/:id/branding" element={<AdminClubBranding />} />
          <Route path="/admin/levels" element={<AdminLevels />} />
          <Route path="/admin/emails" element={<AdminEmails />} />
          <Route path="/admin/requests" element={<AdminRequests />} />
          <Route path="/admin/bulk-import" element={<AdminBulkImport />} />
          <Route path="/portal" element={<Portal />} />
          <Route path="/portal/login" element={<PortalLogin />} />
          <Route path="/portal/set-password" element={<PortalSetPassword />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </AnimatePresence>
  );
};

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <LoadingScreen minDuration={2000} />
        <ScrollProgress />
        <EasterEggs />
        <BrowserRouter>
          <AnimatedRoutes />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;